import {
  CardData,
  NFCTagInterfaceBase,
  NFCTagInterfaceManagerStatus,
  NFCTagInterfaceType,
  parseNFCTagInterfaceUid,
} from '../NFCProxy';

async function readWithTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  timeoutMs: number,
): Promise<ReadableStreamReadResult<Uint8Array>> {
  let timer: ReturnType<typeof setTimeout>;

  const timeoutPromise = new Promise<ReadableStreamReadResult<Uint8Array>>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`Read operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    const result = await Promise.race([reader.read(), timeoutPromise]);
    return result;
  } finally {
    // Clear the timeout if the read completes or is rejected before the timeout
    clearTimeout(timer!);
  }
}

function escapeRegExp(string: string) {
  return string.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`);
}

export class NFCTagSerialInterface implements NFCTagInterfaceBase {
  public name = 'USB Serial';
  public readonly type = NFCTagInterfaceType.USBSerial;
  public supportsManualConnect = false;
  public status = NFCTagInterfaceManagerStatus.Ready;

  public info: SerialPortInfo = {};
  public port?: SerialPort;
  public connected = false;
  public originalDeviceUID: string;

  // Add properties for status monitoring
  private statusReader?: ReadableStreamDefaultReader<Uint8Array>;
  private statusWriter?: WritableStreamDefaultWriter<Uint8Array>;
  private statusMonitoringActive = false;
  private onMessageCallback?: (event: string) => void;
  private onErrorCallback?: (status: string) => void;
  private onOpenCallback?: (status: string) => void;
  private lastStatusSent: string | null = null;
  private serialBuffer = '';

  public constructor(deviceUID: string) {
    this.originalDeviceUID = deviceUID;
    const parsed = parseNFCTagInterfaceUid(deviceUID);
    if (parsed?.type === NFCTagInterfaceType.USBSerial) {
      const usbInfo = parsed.address.split(',');
      if (usbInfo.length === 2) {
        this.info.usbVendorId = Number.parseInt(usbInfo[0]!, 10);
        this.info.usbProductId = Number.parseInt(usbInfo[1]!, 10);
      }
    }
  }

  public dispose(): void {
    this.stopStatusMonitoring();
    void this.port?.close();
  }

  public async init(): Promise<void> {
    try {
      if (!this.port) {
        this.connected = false;
        return;
      }

      if (!this.port.writable || !this.port.readable) {
        return;
      }

      const writer = this.port.writable.getWriter();
      const reader = this.port.readable.getReader();
      const encoder = new TextEncoder();

      await this.port.setSignals({ dataTerminalReady: false });
      await new Promise((r) => {
        setTimeout(r, 100);
      });

      await this.port.setSignals({ dataTerminalReady: true });
      await new Promise((r) => {
        setTimeout(r, 500);
      });

      const crData = encoder.encode('\n');
      await writer.write(crData);

      // Set up reader to look for "nfc>" response
      const decoder = new TextDecoder();
      let buffer = '';

      let searching = true;
      while (searching) {
        try {
          const { value, done } = await readWithTimeout(reader, 1000);
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          if (buffer.includes('nfc>')) {
            // Found the expected response
            this.connected = true;
            this.lastStatusSent = '';

            // Start status monitoring if callbacks are set
            if (this.onMessageCallback) {
              reader.releaseLock();
              writer.releaseLock();
              // Send before starting status monitoring to prevent race conditions
              this.onOpenCallback?.('NFC Serial connected');
              await this.startStatusMonitoring();
              return;
            }

            searching = false;
          }
        } catch (error) {
          if (error instanceof Error && error.message.includes('timed out')) {
            await writer.write(new TextEncoder().encode('\n'));
          } else {
            reader.releaseLock();
            writer.releaseLock();
            throw error;
          }
        }
      }
    } catch {
      // Failed to initialize serial communication
      this.connected = false;
    }
  }

  public async checkCardBlank(uuid: string): Promise<{ success: boolean; status: string }> {
    return await this.executeWithStatusMonitoringPaused(
      async () => {
        try {
          const response = await this.sendSerialCommandAndReadJSON<{
            success: boolean;
            error?: string;
            card?: object;
          }>(`read ${uuid}`);

          if (response.success && response.card && Object.keys(response.card).length === 0) {
            // Card is blank (success: true, card: {})
            return { success: true, status: 'Card is blank' };
          }

          if (!response.success && response.error === 'Failed to read tags') {
            // Card is not blank (success: false, error: "Failed to read tags")
            return { success: false, status: 'NFC badge already has a password' };
          }

          if (response.success && response.card && Object.keys(response.card).length > 0) {
            // Card has data but no password
            return {
              success: true,
              status: 'Card has no password, and is not blank (Can be reprogrammed)',
            };
          }

          // Unexpected response
          return { success: false, status: 'Unexpected response from card' };
        } catch {
          return { success: false, status: 'Failed to communicate with card' };
        }
      },
      { success: false, status: 'Failed to check card status' },
    );
  }

  public async writeCard(cardData: CardData): Promise<{ success: boolean; status: string }> {
    return await this.executeWithStatusMonitoringPaused(
      async () => {
        try {
          // Extract uuid and password from cardData for command parameters
          const { uuid, password, ...jsonData } = cardData;

          // Create the JSON payload without uuid and password
          const jsonPayload = JSON.stringify(jsonData);

          // Format the command: "write <uuid> <password> <json>"
          const command = `write ${uuid} ${password} ${jsonPayload}`;

          const response = await this.sendSerialCommandAndReadJSON<{
            success: boolean;
            error: string;
          }>(command);

          return {
            success: response.success,
            status: response.error || 'Card written successfully',
          };
        } catch {
          return { success: false, status: 'Failed to write card' };
        }
      },
      { success: false, status: 'Failed to write card' },
    );
  }

  public async setPassword(uuid: string, password: number): Promise<{ success: boolean }> {
    return await this.executeWithStatusMonitoringPaused(
      async () => {
        try {
          // Format the command: "set_password <uuid> <password>"
          const command = `set_password ${uuid} ${password}`;

          const response = await this.sendSerialCommandAndReadJSON<{ success: boolean }>(command);
          return response;
        } catch {
          return { success: false };
        }
      },
      { success: false },
    );
  }

  public async getUUID(): Promise<{ success: boolean; uuid: string }> {
    return await this.executeWithStatusMonitoringPaused(
      async () => {
        return await this.sendSerialCommandAndReadJSON<{ success: boolean; uuid: string }>('uuid');
      },
      { success: false, uuid: '' },
    );
  }

  public async reset(): Promise<{ success: boolean }> {
    return await this.executeWithStatusMonitoringPaused(
      async () => {
        return await this.sendSerialCommandAndReadJSON<{ success: boolean }>('reset');
      },
      { success: false },
    );
  }

  public async readCard(
    uuid: string,
    password: number,
  ): Promise<{ success: boolean; card?: CardData }> {
    return await this.executeWithStatusMonitoringPaused(
      async () => {
        try {
          // Format the command: "read <uuid> <password>"
          const command = `read ${uuid} ${password}`;

          const response = await this.sendSerialCommandAndReadJSON<{
            success: boolean;
            card?: CardData;
          }>(command);

          return response;
        } catch {
          return { success: false };
        }
      },
      { success: false },
    );
  }

  public startEventSource(
    onMessage: (event: string) => void,
    onError: (status: string) => void,
    onOpen: (status: string) => void,
  ): () => void {
    this.onMessageCallback = onMessage;
    this.onErrorCallback = onError;
    this.onOpenCallback = onOpen;

    // If already connected, start status monitoring immediately
    if (this.connected) {
      this.startStatusMonitoring()
        .then(() => {
          onOpen('NFC Serial connected');
        })
        .catch(() => {
          onError('Failed to start status monitoring');
        });
    }

    // Return cleanup function
    return () => {
      this.stopStatusMonitoring();
    };
  }

  private async startStatusMonitoring(): Promise<void> {
    if (!this.port || !this.connected || this.statusMonitoringActive) {
      return;
    }

    try {
      if (this.port.writable && this.port.readable) {
        this.statusWriter = this.port.writable.getWriter();
        this.statusReader = this.port.readable.getReader();
        this.statusMonitoringActive = true;

        // Send initial status command
        await this.sendStatusCommand();

        // Start reading status responses (don't await - runs continuously in background)
        void this.readStatusResponses();
      }
    } catch {
      this.onErrorCallback?.('Failed to start status monitoring');
    }
  }

  private async sendStatusCommand(): Promise<void> {
    if (!this.statusWriter) {
      return;
    }

    try {
      const encoder = new TextEncoder();
      const statusCommand = encoder.encode('status\n');
      await this.statusWriter.write(statusCommand);
    } catch {
      this.onErrorCallback?.('Failed to send status command');
    }
  }

  private async readStatusResponses(): Promise<void> {
    if (!this.statusReader) {
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (this.statusMonitoringActive) {
        const { value, done } = await readWithTimeout(this.statusReader, 5000);
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        // Keep incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (
            trimmedLine &&
            !/^(?:nfc>\s*)?status$/.test(trimmedLine) &&
            trimmedLine !== this.lastStatusSent
          ) {
            this.onMessageCallback?.(trimmedLine);
            this.lastStatusSent = trimmedLine;
          }
        }
      }
    } catch {
      if (this.statusMonitoringActive) {
        this.connected = false;
        this.onErrorCallback?.('Status monitoring connection lost');

        // Attempt to restart status monitoring after a delay
        setTimeout(() => {
          if (this.statusMonitoringActive) {
            this.restartStatusMonitoring()
              .then(() => {
                this.onOpenCallback?.('NFC Serial connected');
              })
              .catch(() => {
                this.onErrorCallback?.('Failed to restart status monitoring');
              });
          }
        }, 2000);
      }
    }
  }

  private async restartStatusMonitoring(): Promise<void> {
    this.stopStatusMonitoring();

    // Reinitialize connection
    await this.init();

    if (this.connected) {
      await this.startStatusMonitoring();
      this.onOpenCallback?.('NFC Serial connected');
    }
  }

  private stopStatusMonitoring(): void {
    this.statusMonitoringActive = false;
    // Send a \n to stop the status monitoring
    const encoder = new TextEncoder();
    const statusCommand = encoder.encode('\n');
    // Don't care when this is done.
    void this.statusWriter?.write(statusCommand);

    if (this.statusReader) {
      this.statusReader.releaseLock();
      this.statusReader = undefined;
    }

    if (this.statusWriter) {
      this.statusWriter.releaseLock();
      this.statusWriter = undefined;
    }
  }

  private async executeWithStatusMonitoringPaused<T>(
    fn: () => Promise<T>,
    fallbackResult: T,
  ): Promise<T> {
    if (!this.port || !this.connected) {
      return fallbackResult;
    }

    // Stop status monitoring temporarily
    const wasMonitoring = this.statusMonitoringActive;
    this.stopStatusMonitoring();

    try {
      const result = await fn();
      return result;
    } catch {
      return fallbackResult;
    } finally {
      // Restart status monitoring if it was active before
      if (wasMonitoring) {
        await this.startStatusMonitoring();
      }
    }
  }

  private async sendSerialCommandAndReadJSON<T>(command: string): Promise<T> {
    if (!this.port) {
      throw new Error('Serial port not available');
    }

    // Get writer and reader for the command
    const writer = this.port.writable?.getWriter();
    const reader = this.port.readable?.getReader();

    if (!writer || !reader) {
      throw new Error('Unable to get serial port writer or reader');
    }

    try {
      // Send the command
      const encoder = new TextEncoder();
      const commandBytes = encoder.encode(`${command}\n`);
      await writer.write(commandBytes);

      // First, consume the optional command echo: "nfc>" + spaces + command + "\r\n"
      await this.consumeCommandEcho(reader, command);

      // Read and parse the JSON response
      const result = await this.readJSONFromSerial<T>(reader);
      return result;
    } finally {
      // Release the reader and writer
      reader.releaseLock();
      writer.releaseLock();
    }
  }

  private async consumeCommandEcho(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    originalCommand: string,
  ): Promise<void> {
    const decoder = new TextDecoder();
    let buffer = '';
    const startTime = Date.now();
    const totalTimeoutMs = 2000;

    try {
      // Read in a loop for up to 1 second to handle fragmented responses
      while (Date.now() - startTime < totalTimeoutMs) {
        const remainingTime = totalTimeoutMs - (Date.now() - startTime);
        if (remainingTime <= 0) {
          break;
        }

        const { value, done } = await readWithTimeout(reader, Math.min(remainingTime, 200));
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // This could match towards the end of the buffer if there is a lot of garbage on the front
        // and we want to discard that.
        const echoPattern = new RegExp(`${escapeRegExp(originalCommand)}\\r?\\n`);
        const match = buffer.match(echoPattern);
        if (match?.index !== undefined) {
          this.serialBuffer = buffer.slice(Math.max(0, match.index + match[0].length));
          // Found the command echo, consume it and return
          return;
        }
      }

      // If we didn't find the expected echo pattern, preserve any data we read
      if (buffer) {
        this.serialBuffer = buffer;
      }
    } catch {
      // Timeout or other error - preserve any data we read and continue normally
      if (buffer) {
        this.serialBuffer = buffer;
      }
    }
  }

  private async readJSONFromSerial<T>(reader: ReadableStreamDefaultReader<Uint8Array>): Promise<T> {
    const decoder = new TextDecoder();
    let buffer = this.serialBuffer;

    // Clear the buffered data after using it
    this.serialBuffer = '';

    // Read until we get a complete JSON response
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    while (true) {
      // We read from the serial at the end of this loop since buffer could come in with data from before.
      // Look for complete lines
      const lines = buffer.split(/\r?\n/);
      // Keep incomplete line in buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();
        // Skip empty lines and prompt
        if (trimmedLine && trimmedLine !== 'nfc>') {
          // Try to parse as JSON
          try {
            const parsed: unknown = JSON.parse(trimmedLine);
            if (parsed && typeof parsed === 'object' && 'success' in parsed) {
              return parsed as T;
            }
          } catch {
            // Not JSON, continue reading
          }
        }
      }

      const { value, done } = await readWithTimeout(reader, 1000);
      if (done) {
        throw new Error('Serial stream ended before receiving JSON response');
      }

      buffer += decoder.decode(value, { stream: true });
    }
  }
}
